var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useState } from 'react';
import { Input } from '../../../../../Forms/Input/Input';
import { useClickOutside } from '../../../../Modal/hooks/useClickOutside';
import { useEvent } from '../../../hooks/useEvent';
import IconList from '../../IconList';
import { i18n } from '@lingui/core';
var OverlayComponentDefault = function (_a) {
    var children = _a.children;
    return _jsx("div", { children: children });
};
var createNumberComparators = function () { return [
    { label: i18n._({ id: 'dataGrid.comparator.equals', message: 'Equals' }), value: 'eq', icon: 'equals' },
    { label: i18n._({ id: 'dataGrid.comparator.notEquals', message: 'Not equal' }), value: 'neq', icon: 'notEqual' },
    { label: i18n._({ id: 'dataGrid.comparator.lessThan', message: 'Less than' }), value: 'lt', icon: 'lessThen' },
    {
        label: i18n._({ id: 'dataGrid.comparator.lessThanOrEquals', message: 'Less than or equals' }),
        value: 'lte',
        icon: 'lessThanEqual'
    },
    { label: i18n._({ id: 'dataGrid.comparator.greaterThan', message: 'Greater than' }), value: 'gt', icon: 'greaterThan' },
    {
        label: i18n._({ id: 'dataGrid.comparator.greaterThanOrEquals', message: 'Greater than or equal' }),
        value: 'gte',
        icon: 'greaterThanEqual'
    }
]; };
var createEqualComparators = [
    { label: i18n._({ id: 'dataGrid.comparator.equals', message: 'Equals' }), value: 'eq', icon: 'equals' },
    { label: i18n._({ id: 'dataGrid.comparator.notEquals', message: 'Not equal' }), value: 'neq', icon: 'notEqual' }
];
var normalizeValue = function (number, isFloat, decimals) {
    var normalized = String(number).replace(',', '.').replace(/[^0-9.]/g, '');
    if (isFloat || !normalized) {
        return normalized;
    }
    return !decimals ? parseInt(normalized, 10) : (parseFloat(normalized) || 0).toFixed(decimals).substr(0, String(number).length);
};
var NumberRange = function (props) {
    var _a;
    var numberComparators = createNumberComparators();
    var name = props.name, _b = props.value, value = _b === void 0 ? {} : _b, label = props.label, onChange = props.onChange, decimals = props.decimals, isFloat = props.isFloat, className = props.className, _c = props.OverlayComponent, OverlayComponent = _c === void 0 ? OverlayComponentDefault : _c, comparators = props.comparators, defaultComparator = props.defaultComparator, isEqual = props.isEqual, rest = __rest(props, ["name", "value", "label", "onChange", "decimals", "isFloat", "className", "OverlayComponent", "comparators", "defaultComparator", "isEqual"]);
    var _d = useState(false), isOpen = _d[0], setOpen = _d[1];
    var comparator = (value === null || value === void 0 ? void 0 : value.comparator) ||
        defaultComparator ||
        ((_a = comparators === null || comparators === void 0 ? void 0 : comparators[0]) === null || _a === void 0 ? void 0 : _a.value) ||
        numberComparators[0].value;
    var defaultComparators = isEqual ? createEqualComparators : numberComparators;
    var comparatorOptions = comparators || defaultComparators;
    var fieldValue = (value === null || value === void 0 ? void 0 : value.value) || (value === null || value === void 0 ? void 0 : value.value) === 0 ? value.value : '';
    var onClose = useCallback(function () { return setOpen(false); }, []);
    var pickerRef = useClickOutside({ onClose: onClose });
    var togglePicker = useCallback(function () { return setOpen(!isOpen); }, [isOpen, setOpen]);
    var onChangeTextInput = useEvent(function (event) {
        // @TODO find better way to detect between comparator
        if (comparator === 'between') {
            var values = String(event.target.value).split(',');
            // to many ',', invalid value
            if (values.length > 2) {
                onChange({ comparator: comparator, value: '' });
                return;
            }
            // two values for between comparation
            if (values.length === 2) {
                var normalizedValues = values.map(function (value) { return normalizeValue(value, false, 0); });
                onChange({ comparator: comparator, value: normalizedValues.join(',') });
                return;
            }
        }
        var number = normalizeValue(event.target.value, isFloat, decimals);
        var isValid = number || number === 0;
        onChange({ comparator: comparator, value: isValid ? number : '' });
    });
    var onChangeComparator = useEvent(function (option) {
        onChange({ comparator: option.value, value: fieldValue });
    });
    return (_jsxs("div", { ref: pickerRef, children: [_jsx(Input, __assign({ name: name, value: fieldValue, label: label, "data-cy": "".concat(name, "Inp"), onChange: onChangeTextInput, iconOnClick: togglePicker, icon: "ellipsisVertical", iconPlacement: "right", type: "text", className: className, autoComplete: "off" }, rest)), isOpen && (_jsx(OverlayComponent, { children: _jsx(IconList, { list: comparatorOptions, selected: comparator, onClick: onChangeComparator }) }))] }));
};
export default NumberRange;
